/**
 * CTSContentEditingPure
 *
 * Pure component for editing cadence template step content.
 * Handles different step types with appropriate editors:
 * - visit/follow: Static text (no editing)
 * - likeRecentPosts: Number input
 * - reminder: ManualSelectPure for action selection
 * - message/connect/call: TipTapInput body only
 * - inmail/email: TipTapInput subject + body
 * - sift: Render prop for custom condition builder
 */
import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';

import type { SwitchBand } from '@poseidon/types/types/switchBand.type';

import type { TipTapDocument, TipTapVariable } from '../Input2/TipTapInput';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import { cn } from '../cn';
import { ContentDivider } from '../ContentDivider';
import { Button, FilterSelect, Input, Textarea, ToggleSwitch } from '../Input2';
import TipTapInput from '../Input2/TipTapInput';
import { ManualSelectPure } from '../ManualSelect/ManualSelectPure';
import { Arrows, General } from '../StrokeIcons';

/**
 * Seed bands for a NEW `switch` step: one editable range (0+) plus the catch-all. The rep refines
 * the ranges and adds more branches directly on the graph, so the editor only picks the attribute.
 */
const DEFAULT_SWITCH_BANDS: SwitchBand[] = [
  { option: 'one', min: 0, max: null, label: null, isCatchAll: false },
  { option: 'six', min: null, max: null, label: null, isCatchAll: true },
];

// ============================================================================
// Types
// ============================================================================

export type StepType =
  | 'visit'
  | 'follow'
  | 'likeRecentPosts'
  | 'call'
  | 'reminder'
  | 'message'
  | 'connect'
  | 'inmail'
  | 'email'
  | 'sift'
  | 'voice_note'
  | 'switch_campaign'
  | 'switch';

export type SiftConfig = {
  conditions: Array<{
    field: string;
    operator: string;
    value: string;
  }>;
};

export type StepData = {
  subject?: string | null;
  body?: string | null;
  postsToLike?: number | null;
  cc?: string | null;
  bcc?: string | null;
  /**
   * Email step only: when true, the step continues the previous email's thread and
   * reuses its subject instead of starting a new one. Defaults true; the editor only
   * surfaces the toggle when an earlier email step exists (see `previousEmailSubject`).
   */
  replyToPrevious?: boolean | null;
  /**
   * Connect step only: when true (the default), a still-pending LinkedIn invitation is
   * auto-withdrawn once this connect step's not-accepted ("No") branch resolves. Surfaced as a
   * toggle only for `connect` steps.
   */
  autoWithdraw?: boolean | null;
  siftConditions?: SiftConfig | null;
  /**
   * InMail-only flag: when true the step is sent ONLY when LinkedIn
   * marks the recipient as "Open Profile" (so it doesn't consume an
   * InMail credit). False / unset → send regardless, consuming a
   * credit when needed.
   */
  onlyOpenLink?: boolean | null;
  /**
   * Per-step AI personalization. When `isPersonalized` is true, Claude
   * drafts the body (and subject for inmail/email) per contact at the
   * candidate-review stage using `personalizationPrompt` as the
   * instruction. The static body / subject become irrelevant for this
   * step — they're overridden by the approved draft at promotion.
   */
  isPersonalized?: boolean | null;
  personalizationPrompt?: string | null;
  /**
   * Touch-campaign step chaining: PUBLIC ids of the campaign templates to enroll
   * the contact into when this step completes (empty for no handoff). This legacy
   * editor edits a single target (the first); the touch-campaign graph edits many.
   */
  triggerCadenceTemplateIds?: string[];
  /**
   * `switch` step only: the numeric attribute key (e.g. 'age' / 'aum') the switch
   * routes on, and the branch bands (up to 6 arms) that map ranges to arms.
   */
  switchAttribute?: string | null;
  switchBands?: SwitchBand[] | null;
};

export type StepErrors = {
  default?: string;
  body?: string;
  subject?: string;
  postsToLike?: string;
};

export type CTSContentEditingPureProps = {
  /** Step type determines which editor to show */
  stepType: StepType;
  /** Initial/current step data */
  stepData: StepData;
  /** Validation errors */
  errors?: StepErrors;
  /** Variables for @ mentions in TipTapInput */
  variables?: TipTapVariable[];
  /** Documents for / mentions in TipTapInput */
  documents?: TipTapDocument[];
  /** Whether to show { comment mentions */
  showComments?: boolean;
  /** Whether to show / document mentions */
  showDocuments?: boolean;
  /** Callback when user clicks "Create new document" */
  onCreateDocument?: () => void;
  /** Whether to allow formatting (bold, lists, etc.) */
  allowFormatting?: boolean;
  /** Whether to allow links */
  allowLinks?: boolean;
  /** Callback when step is submitted */
  onSubmit: (data: StepData) => void | Promise<void>;
  /** Callback when editing is cancelled */
  onCancel: () => void;
  /**
   * Callback to delete the step. When set, a trash button renders in the
   * editor header (right side) with a "Delete this step" tooltip.
   */
  onDelete?: () => void;
  /** Whether submit is in progress */
  isSubmitting?: boolean;
  /** Custom submit button text */
  submitLabel?: string;
  /** Custom cancel button text */
  cancelLabel?: string;

  // Reminder-specific props
  /** Available reminder actions (for reminder step type) */
  reminderActions?: ReminderAction[];
  /** Whether reminder actions are loading */
  reminderActionsLoading?: boolean;
  /** Callback to create a new reminder action */
  onCreateReminder?: (name: string) => Promise<void>;
  /** Whether a reminder is being created */
  reminderCreating?: boolean;
  /** Callback for reminder errors */
  onReminderError?: (message: string) => void;

  // Sift-specific render prop
  /** Render prop for sift condition builder */
  renderSiftConditionBuilder?: (props: { value: SiftConfig; onChange: (value: SiftConfig) => void }) => ReactNode;

  /**
   * Whether the rep can toggle "Personalize per contact" on a step.
   * Hides the toggle row entirely when false. Gated by the team's
   * `campaign_prefilter_enabled` flag — personalization only makes
   * sense for users on the candidate-staging flow (where AI drafts get
   * generated + reviewed before promotion). Defaults to true so legacy
   * call sites unaffected.
   */
  canPersonalize?: boolean;

  /**
   * Optional model-info to display in the "What we'll send to the AI"
   * explainer (admin-only). Hidden when null/undefined. The wrapper is
   * expected to fetch the resolved model id from `ai_model` and gate
   * visibility on `userTeam.isSuperAdmin || auth.impersonation != null`.
   */
  personalizeModelInfo?: { modelId: string } | null;

  /**
   * Touch-campaign step chaining. When true, an action step shows a
   * "When this step completes → start campaign" picker. Gated to touch
   * campaigns by the wrapper (only there does a single completing step map
   * cleanly to a follow-on campaign). Defaults to false.
   */
  showTriggerCampaign?: boolean;
  /**
   * Options for the trigger-campaign picker (the team's other templates).
   * `value` is the PUBLIC template id; `label` is the template name.
   */
  campaignTemplateOptions?: { value: string; label: string }[];

  /**
   * `switch` step only: the selectable numeric attributes the switch can route on
   * (from the server's switch-attribute registry). `unit` is shown after the range
   * inputs (e.g. "years" / "USD").
   */
  switchAttributeOptions?: { value: string; label: string; unit: string | null }[];

  /**
   * Subject of the email step that runs immediately before this one, or null when this
   * is the first email in the sequence. When non-null (email steps only) the editor
   * shows a "Continue previous email thread" toggle; turning it on hides the Subject
   * field and inherits this subject so the send keeps the same thread.
   */
  previousEmailSubject?: string | null;

  // Styling
  className?: string;
  cardClassName?: string;
};

// ============================================================================
// Helpers
// ============================================================================

/** Strip HTML tags from text */
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

/** Check if text content is non-empty */
function isNotEmpty(text: string | null | undefined): boolean {
  return stripHtml(text).length > 0;
}

// ============================================================================
// Footer Component
// ============================================================================

type FooterProps = {
  onCancel: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  hideSubmit?: boolean;
  hideCancel?: boolean;
};

function Footer({ onCancel, submitLabel, cancelLabel, isSubmitting, hideSubmit, hideCancel }: FooterProps) {
  return (
    <div className="flex gap-1.5 pt-2">
      <div className="grow" />
      {!hideCancel && (
        <Button size="sm" text onClick={onCancel} disabled={isSubmitting}>
          {cancelLabel ?? 'Cancel'}
        </Button>
      )}
      {!hideSubmit && (
        <Button icon={<General.Check />} type="submit" size="sm" color="blue" disabled={isSubmitting}>
          {submitLabel ?? 'Update'}
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// EditorHeader — step-type title on the left, cancel button on the right.
// ============================================================================

const STEP_TYPE_LABELS: Record<string, string> = {
  connect: 'Connect',
  message: 'Message',
  inmail: 'InMail',
  email: 'Email',
  reminder: 'Reminder',
  sift: 'Filter',
  visit: 'Visit',
  follow: 'Follow',
  likeRecentPosts: 'Like',
  call: 'Call',
  voice_note: 'Voice note',
  switch_campaign: 'Change to campaign',
  switch: 'Switch',
};

function EditorHeader({
  stepType,
  onDelete,
  isSubmitting,
}: {
  stepType: StepType;
  onDelete?: () => void;
  isSubmitting?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between border-b border-slate-200 px-3 py-1.5 dark:border-charade-700"
      data-testid="cts-editor-header">
      <span
        data-testid="cts-editor-header-title"
        className="py-1.5 text-xs font-semibold uppercase tracking-wide text-lynch-700 dark:text-lynch-300">
        {STEP_TYPE_LABELS[stepType] ?? stepType}
      </span>
      {onDelete != null && (
        <Button
          data-testid="cts-editor-header-delete"
          icon={<General.Trash01 />}
          size="2xs"
          color="red"
          tone="soft"
          onClick={onDelete}
          disabled={isSubmitting}
          tooltip="Delete this step">
          delete
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// FieldRow — prefixes every input with a small label so the user always
// knows what field they're typing in (matches Gmail-style "To:/Cc:/Subject:"
// inline labels).
// ============================================================================

function FieldRow({
  label,
  children,
}: {
  label: string;
  /** kept for backward compat; layout is now always stacked. */
  alignTop?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col" data-testid={`cts-field-row-${label.toLowerCase()}`}>
      <span className="select-none px-3 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-lynch-500 dark:text-lynch-400">
        {label}
      </span>
      <div className="min-w-0 w-full">{children}</div>
    </div>
  );
}

/**
 * A {@link FieldRow} whose body is a single description + toggle. Both the
 * "Free only" and "Personalize per contact" rows share this so their text and
 * toggle stay aligned with each other and with the section label above them
 * (matching the label's `px-3`). Don't inline a bespoke flex row per toggle —
 * that's how the padding drifted and the toggles ended up misaligned.
 */
function ToggleFieldRow({
  label,
  description,
  checked,
  onChange,
  testId,
  toggleTestId,
}: {
  label: string;
  description: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId?: string;
  toggleTestId?: string;
}) {
  return (
    <FieldRow label={label}>
      <div data-testid={testId} className="flex items-center justify-between gap-3 px-3 pb-2">
        <span className="text-xs text-lynch-700 dark:text-lynch-300">{description}</span>
        <ToggleSwitch data-testid={toggleTestId} size="sm" color="blue" checked={checked} onChange={onChange} />
      </div>
    </FieldRow>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function CTSContentEditingPure({
  stepType,
  stepData,
  errors,
  variables = [],
  documents = [],
  showComments = true,
  showDocuments = true,
  onCreateDocument,
  allowFormatting = false,
  allowLinks = false,
  onSubmit,
  onCancel,
  onDelete,
  isSubmitting = false,
  submitLabel,
  cancelLabel,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  renderSiftConditionBuilder,
  canPersonalize = true,
  personalizeModelInfo = null,
  showTriggerCampaign = false,
  campaignTemplateOptions = [],
  switchAttributeOptions = [],
  previousEmailSubject = null,
  className,
  cardClassName,
}: CTSContentEditingPureProps) {
  // Local state for editing
  const [localData, setLocalData] = useState<StepData>(() => ({
    subject: stepData.subject ?? '',
    body: stepData.body ?? '',
    postsToLike: stepType === 'likeRecentPosts' ? (stepData.postsToLike ?? 1) : undefined,
    cc: stepData.cc ?? '',
    bcc: stepData.bcc ?? '',
    replyToPrevious: stepData.replyToPrevious ?? true,
    autoWithdraw: stepData.autoWithdraw ?? true,
    siftConditions: stepType === 'sift' ? (stepData.siftConditions ?? { conditions: [] }) : undefined,
    onlyOpenLink: stepType === 'inmail' ? (stepData.onlyOpenLink ?? false) : undefined,
    isPersonalized: stepData.isPersonalized ?? false,
    personalizationPrompt: stepData.personalizationPrompt ?? '',
    triggerCadenceTemplateIds: stepData.triggerCadenceTemplateIds ?? [],
    switchAttribute: stepType === 'switch' ? (stepData.switchAttribute ?? '') : undefined,
  }));
  const [localErrors, setLocalErrors] = useState<StepErrors | undefined>(errors);

  // Email thread-continuation. Only meaningful once an earlier email exists in the
  // sequence (the wrapper passes its subject via `previousEmailSubject`). When on,
  // this step hides its own Subject field and inherits the previous subject — the
  // server reuses that subject and threads the send via In-Reply-To / thread id.
  const showReplyToggle = stepType === 'email' && previousEmailSubject != null;
  const threadContinued = showReplyToggle && (localData.replyToPrevious ?? true);

  const hasError = errors?.default != null || errors?.body != null || errors?.subject != null;

  // Validate and submit
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Validate based on step type. Personalized steps skip the static
    // body/subject required checks — Claude drafts those per contact at
    // the candidate-review stage, so empty here is intentional.
    if (!localData.isPersonalized && ['inmail', 'email'].includes(stepType)) {
      // A thread-continuing email inherits the previous step's subject, so its own
      // Subject field is hidden and not required (the server allows blank there too).
      const subjectRequired = !threadContinued;
      if ((subjectRequired && !isNotEmpty(localData.subject)) || !isNotEmpty(localData.body)) {
        setLocalErrors({
          body: !isNotEmpty(localData.body) ? 'Body is required' : undefined,
          subject: subjectRequired && !isNotEmpty(localData.subject) ? 'Subject is required' : undefined,
        });
        return;
      }
    } else if (!localData.isPersonalized && ['message', 'reminder'].includes(stepType)) {
      if (!isNotEmpty(localData.body)) {
        setLocalErrors({ body: 'Body is required' });
        return;
      }
    } else if (stepType === 'likeRecentPosts' && (localData.postsToLike ?? 0) < 1) {
      setLocalErrors({ postsToLike: 'Posts to like value should be greater than 0' });
      return;
    } else if (stepType === 'switch_campaign' && (localData.triggerCadenceTemplateIds?.length ?? 0) === 0) {
      // A switch step with no destination is a no-op — require a target campaign.
      setLocalErrors({ default: 'Select a campaign to switch to' });
      return;
    } else if (stepType === 'switch' && !localData.switchAttribute) {
      // The editor only picks the attribute; ranges are edited on the graph (seeded with a default).
      setLocalErrors({ default: 'Pick an attribute to route by' });
      return;
    }

    // Submit the RAW TipTap HTML — never strip tags. The server stores
    // body2 as HTML so it can re-render variable mention spans
    // (`<span data-id="firstName">…</span>`), document chips, etc. on
    // every read. Stripping tags here flattens those mentions to plain
    // text and the variable identity is lost forever.
    //
    // Always coerce to a string (NEVER `undefined`) so the host's
    // `typeof === 'string'` gate accepts the field. An EMPTY string is
    // a meaningful value — connect steps support sending no message
    // ("empty connection request"), and the user must be able to clear
    // a body and have that clear actually persist. Validation above
    // already rejected empty for types where empty isn't allowed
    // (inmail/email/message/reminder), so by this point a `''` body
    // here means the user explicitly chose to send nothing.
    setLocalErrors(undefined);
    await onSubmit({
      // When continuing a thread the Subject field is hidden, so persist the inherited
      // subject (matches the send-time behaviour and keeps the stored value meaningful).
      subject: threadContinued ? (previousEmailSubject ?? '') : (localData.subject ?? ''),
      body: localData.body ?? '',
      postsToLike: localData.postsToLike,
      cc: localData.cc || undefined,
      bcc: localData.bcc || undefined,
      siftConditions: localData.siftConditions,
      onlyOpenLink: localData.onlyOpenLink,
      isPersonalized: localData.isPersonalized ?? false,
      personalizationPrompt: localData.personalizationPrompt ?? '',
      triggerCadenceTemplateIds: localData.triggerCadenceTemplateIds ?? [],
      replyToPrevious: stepType === 'email' ? (localData.replyToPrevious ?? true) : undefined,
      autoWithdraw: stepType === 'connect' ? (localData.autoWithdraw ?? true) : undefined,
      switchAttribute: stepType === 'switch' ? localData.switchAttribute || undefined : undefined,
      // Keep existing bands on edit; seed a default (one range + catch-all) for a brand-new switch.
      switchBands:
        stepType === 'switch'
          ? (stepData.switchBands ?? []).some((b) => !b.isCatchAll)
            ? stepData.switchBands!
            : DEFAULT_SWITCH_BANDS
          : undefined,
    });
  };

  // Bg matches the input default (slate-50) so labels and inputs read as one
  // continuous surface inside the card — no two-tone visual.
  const cardClasses = cn(
    'rounded-lg border border-slate-200 dark:border-charade-700 bg-slate-50 dark:bg-charade-900 overflow-hidden',
    cardClassName,
  );

  const containerClasses = cn('flex flex-col gap-2', hasError && 'ring-1 ring-fair-pink-500', className);

  // Render different editors based on step type
  switch (stepType) {
    case 'visit':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <div data-testid="cts-visit-description" className="px-3 py-3 text-sm text-lynch-700 dark:text-lynch-300">
              Will visit the profile page. There's nothing to configure — use{' '}
              <span className="font-semibold">delete</span> in the header to remove this step.
            </div>
          </div>
          <Footer
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
            isSubmitting={isSubmitting}
            hideSubmit
          />
        </form>
      );

    case 'follow':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <div data-testid="cts-follow-description" className="px-3 py-3 text-sm text-lynch-700 dark:text-lynch-300">
              Will follow the profile page. There's nothing to configure — use{' '}
              <span className="font-semibold">delete</span> in the header to remove this step.
            </div>
            {showTriggerCampaign && (
              <TriggerCampaignField
                value={localData.triggerCadenceTemplateIds?.[0] ?? null}
                options={campaignTemplateOptions}
                onChange={(v) => setLocalData((d) => ({ ...d, triggerCadenceTemplateIds: v == null ? [] : [v] }))}
              />
            )}
          </div>
          <Footer
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
            isSubmitting={isSubmitting}
            // Only a no-config step when there's nothing to save; with the
            // trigger-campaign picker shown, the user needs a submit button.
            hideSubmit={!showTriggerCampaign}
          />
        </form>
      );

    case 'likeRecentPosts':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <FieldRow label="Posts to like">
              <Input
                data-testid="cts-like-posts-to-like"
                type="number"
                min={1}
                placeholder="Number of posts"
                error={localErrors?.postsToLike}
                value={localData.postsToLike ?? 1}
                onChange={(e) => setLocalData((d) => ({ ...d, postsToLike: Number(e.target.value) }))}
                bordered={false}
                className="w-full rounded-none"
              />
            </FieldRow>
            <div className="px-3 pb-2 pt-1 text-xs text-lynch-600 dark:text-lynch-400">
              Will like up to <strong>{localData.postsToLike ?? 1}</strong>{' '}
              {(localData.postsToLike ?? 1) === 1 ? 'post' : 'posts'}
            </div>
            {showTriggerCampaign && (
              <TriggerCampaignField
                value={localData.triggerCadenceTemplateIds?.[0] ?? null}
                options={campaignTemplateOptions}
                onChange={(v) => setLocalData((d) => ({ ...d, triggerCadenceTemplateIds: v == null ? [] : [v] }))}
              />
            )}
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );

    case 'reminder':
      return (
        <div className={containerClasses}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <FieldRow label="Action">
              <div className="px-3 pb-2">
                <ManualSelectPure
                  value={localData.body ?? undefined}
                  reminderActions={reminderActions}
                  isLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder}
                  isCreating={reminderCreating}
                  onError={onReminderError}
                  fullWidth
                  onChange={async (body) => {
                    setLocalData((d) => ({ ...d, body }));
                    await onSubmit({ ...localData, body });
                  }}
                />
              </div>
            </FieldRow>
          </div>
          <Footer
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
            isSubmitting={isSubmitting}
            hideSubmit
          />
        </div>
      );

    case 'call':
    case 'message':
    case 'connect':
    case 'voice_note': {
      const supportsPersonalize = canPersonalize && stepType === 'message';
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            {supportsPersonalize && localData.isPersonalized ? (
              <PersonalizationPromptField
                value={localData.personalizationPrompt ?? ''}
                onChange={(v) => setLocalData((d) => ({ ...d, personalizationPrompt: v }))}
                modelInfo={personalizeModelInfo}
              />
            ) : (
              <FieldRow label="Body">
                <TipTapInput
                  placeholder="Body"
                  value={localData.body ?? ''}
                  onChange={(html) => setLocalData((d) => ({ ...d, body: html }))}
                  error={localErrors?.body}
                  variables={variables}
                  documents={showDocuments ? documents : []}
                  showComments={showComments}
                  onCreateDocument={onCreateDocument}
                  allowFormatting={stepType === 'call' || allowFormatting}
                  allowLinks={allowLinks}
                  autoFocus
                  rows={3}
                  bordered={false}
                  shape="square"
                />
              </FieldRow>
            )}
            {supportsPersonalize && (
              <>
                <ContentDivider />
                <PersonalizeToggleRow
                  checked={localData.isPersonalized ?? false}
                  onChange={(checked) => setLocalData((d) => ({ ...d, isPersonalized: checked }))}
                />
              </>
            )}
            {/* Connect only (this block is shared with voice_note): auto-withdraw the pending
                invitation when the "No" (not-accepted) branch resolves. Defaults on. */}
            {stepType === 'connect' && (
              <>
                <ContentDivider />
                <ToggleFieldRow
                  label="Auto-withdraw"
                  description="Withdraw the invite if it isn't accepted"
                  checked={localData.autoWithdraw ?? true}
                  onChange={(checked) => setLocalData((d) => ({ ...d, autoWithdraw: checked }))}
                  testId="auto-withdraw-row"
                  toggleTestId="auto-withdraw-toggle"
                />
              </>
            )}
            {/* `call` stays action-required after it runs, so its completion
                doesn't fire the trigger — don't offer the control there. */}
            {showTriggerCampaign && stepType !== 'call' && (
              <TriggerCampaignField
                value={localData.triggerCadenceTemplateIds?.[0] ?? null}
                options={campaignTemplateOptions}
                onChange={(v) => setLocalData((d) => ({ ...d, triggerCadenceTemplateIds: v == null ? [] : [v] }))}
              />
            )}
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );
    }

    case 'inmail':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            {canPersonalize && localData.isPersonalized ? (
              <PersonalizationPromptField
                value={localData.personalizationPrompt ?? ''}
                onChange={(v) => setLocalData((d) => ({ ...d, personalizationPrompt: v }))}
                modelInfo={personalizeModelInfo}
              />
            ) : (
              <>
                <FieldRow label="Subject">
                  <TipTapInput
                    placeholder="Subject"
                    value={localData.subject ?? ''}
                    onChange={(html) => setLocalData((d) => ({ ...d, subject: html }))}
                    error={localErrors?.subject}
                    variables={variables}
                    documents={[]}
                    showComments={showComments}
                    autoFocus={!isNotEmpty(stepData.subject)}
                    rows={1}
                    bordered={false}
                    shape="square"
                  />
                </FieldRow>
                <ContentDivider />
                <FieldRow label="Body" alignTop>
                  <TipTapInput
                    placeholder="Body"
                    value={localData.body ?? ''}
                    onChange={(html) => setLocalData((d) => ({ ...d, body: html }))}
                    error={localErrors?.body}
                    variables={variables}
                    documents={showDocuments ? documents : []}
                    showComments={showComments}
                    onCreateDocument={onCreateDocument}
                    autoFocus={isNotEmpty(stepData.subject)}
                    rows={3}
                    bordered={false}
                    shape="square"
                  />
                </FieldRow>
              </>
            )}
            <ContentDivider />
            <ToggleFieldRow
              label="Free only"
              description="Only send when no InMail credit is needed (Open Profile recipients)"
              checked={localData.onlyOpenLink ?? false}
              onChange={(checked) => setLocalData((d) => ({ ...d, onlyOpenLink: checked }))}
              testId="cts-inmail-only-open-link"
              toggleTestId="cts-inmail-only-open-link-toggle"
            />
            {canPersonalize && (
              <>
                <ContentDivider />
                <PersonalizeToggleRow
                  checked={localData.isPersonalized ?? false}
                  onChange={(checked) => setLocalData((d) => ({ ...d, isPersonalized: checked }))}
                />
              </>
            )}
            {showTriggerCampaign && (
              <TriggerCampaignField
                value={localData.triggerCadenceTemplateIds?.[0] ?? null}
                options={campaignTemplateOptions}
                onChange={(v) => setLocalData((d) => ({ ...d, triggerCadenceTemplateIds: v == null ? [] : [v] }))}
              />
            )}
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );

    case 'email':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cn(cardClasses, 'flex flex-col')}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            {canPersonalize && localData.isPersonalized ? (
              <PersonalizationPromptField
                value={localData.personalizationPrompt ?? ''}
                onChange={(v) => setLocalData((d) => ({ ...d, personalizationPrompt: v }))}
                modelInfo={personalizeModelInfo}
              />
            ) : (
              <>
                {threadContinued && (
                  <div
                    data-testid="cts-email-thread-banner"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-lynch-600 dark:text-lynch-300">
                    <Arrows.CornerUpLeft className="size-4 shrink-0 text-green-500" />
                    <span className="min-w-0 truncate">
                      Continues the previous email thread
                      {(previousEmailSubject ?? '').trim().length > 0 && (
                        <>
                          {' · '}
                          <span className="text-lynch-800 dark:text-lynch-100">{previousEmailSubject}</span>
                        </>
                      )}
                    </span>
                  </div>
                )}
                {!threadContinued && (
                  <FieldRow label="Subject">
                    <TipTapInput
                      placeholder="Subject"
                      value={localData.subject ?? ''}
                      onChange={(html) => setLocalData((d) => ({ ...d, subject: html }))}
                      error={localErrors?.subject}
                      variables={variables}
                      documents={[]}
                      showComments={showComments}
                      allowLinks={allowLinks}
                      autoFocus={!isNotEmpty(stepData.subject)}
                      rows={1}
                      bordered={false}
                      shape="square"
                    />
                  </FieldRow>
                )}
                <ContentDivider />
                <FieldRow label="Cc">
                  <Input
                    data-testid="cts-email-cc"
                    placeholder="email@example.com, …"
                    value={localData.cc ?? ''}
                    onChange={(e) => setLocalData((d) => ({ ...d, cc: e.target.value }))}
                    bordered={false}
                    className="w-full rounded-none"
                  />
                </FieldRow>
                <ContentDivider />
                <FieldRow label="Bcc">
                  <Input
                    data-testid="cts-email-bcc"
                    placeholder="email@example.com, …"
                    value={localData.bcc ?? ''}
                    onChange={(e) => setLocalData((d) => ({ ...d, bcc: e.target.value }))}
                    bordered={false}
                    className="w-full rounded-none"
                  />
                </FieldRow>
                <ContentDivider />
                <FieldRow label="Body" alignTop>
                  <TipTapInput
                    placeholder="Body"
                    value={localData.body ?? ''}
                    onChange={(html) => setLocalData((d) => ({ ...d, body: html }))}
                    error={localErrors?.body}
                    variables={variables}
                    documents={showDocuments ? documents : []}
                    showComments={showComments}
                    onCreateDocument={onCreateDocument}
                    allowFormatting
                    allowLinks
                    autoFocus={isNotEmpty(stepData.subject)}
                    rows={5}
                    bordered={false}
                    shape="square"
                  />
                </FieldRow>
              </>
            )}
            {showReplyToggle && (
              <>
                <ContentDivider />
                <ToggleFieldRow
                  label="Thread"
                  description="Continue the previous email thread (reuses its subject)."
                  checked={localData.replyToPrevious ?? true}
                  onChange={(checked) => setLocalData((d) => ({ ...d, replyToPrevious: checked }))}
                  testId="cts-email-reply-toggle"
                  toggleTestId="cts-email-reply-toggle-switch"
                />
              </>
            )}
            {canPersonalize && (
              <>
                <ContentDivider />
                <PersonalizeToggleRow
                  checked={localData.isPersonalized ?? false}
                  onChange={(checked) => setLocalData((d) => ({ ...d, isPersonalized: checked }))}
                />
              </>
            )}
            {showTriggerCampaign && (
              <TriggerCampaignField
                value={localData.triggerCadenceTemplateIds?.[0] ?? null}
                options={campaignTemplateOptions}
                onChange={(v) => setLocalData((d) => ({ ...d, triggerCadenceTemplateIds: v == null ? [] : [v] }))}
              />
            )}
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );

    case 'sift':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <FieldRow label="Conditions">
              <div className="px-3 pb-2">
                {renderSiftConditionBuilder &&
                  renderSiftConditionBuilder({
                    value: localData.siftConditions ?? { conditions: [] },
                    onChange: (siftConditions) => setLocalData((d) => ({ ...d, siftConditions })),
                  })}
                {!renderSiftConditionBuilder && (
                  <div data-testid="cts-sift-no-builder" className="text-xs italic text-lynch-600 dark:text-lynch-400">
                    Sift condition builder not available
                  </div>
                )}
              </div>
            </FieldRow>
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );

    case 'switch_campaign':
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <div className="px-3 pt-2 text-xs text-lynch-600 dark:text-lynch-400">
              When this branch runs, the contact leaves this campaign and starts the campaign below.
            </div>
            <FieldRow label="Switch to campaign">
              <div className="px-3 pb-2" data-testid="cts-switch-campaign">
                <FilterSelect
                  options={campaignTemplateOptions}
                  value={localData.triggerCadenceTemplateIds?.[0] ?? ''}
                  onChange={(v) => setLocalData((d) => ({ ...d, triggerCadenceTemplateIds: v ? [v] : [] }))}
                  placeholder="Select a campaign…"
                  className="w-full"
                />
                {localErrors?.default != null && (
                  <div
                    data-testid="cts-switch-campaign-error"
                    className="pt-1 text-xs text-fair-pink-600 dark:text-fair-pink-400">
                    {localErrors.default}
                  </div>
                )}
              </div>
            </FieldRow>
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );

    case 'switch': {
      return (
        <form className={containerClasses} onSubmit={handleSubmit}>
          <div className={cardClasses}>
            <EditorHeader stepType={stepType} onDelete={onDelete} isSubmitting={isSubmitting} />
            <div className="px-3 pt-2 text-xs text-lynch-600 dark:text-lynch-400">
              When this branch runs, the contact is routed down one arm based on a number. Contacts that don't match any
              range take the "Everything else" arm.
            </div>
            <FieldRow label="Route by">
              <div className="px-3 pb-2" data-testid="cts-switch-attribute">
                <FilterSelect
                  options={switchAttributeOptions}
                  value={localData.switchAttribute ?? ''}
                  onChange={(v) => setLocalData((d) => ({ ...d, switchAttribute: v || '' }))}
                  placeholder="Select an attribute…"
                  className="w-full"
                />
              </div>
            </FieldRow>
            <div className="px-3 pb-2 text-xs text-lynch-500 dark:text-lynch-400" data-testid="cts-switch-bands-note">
              Save, then edit each branch's range and add more branches directly on the graph.
            </div>
            {localErrors?.default != null && (
              <div data-testid="cts-switch-error" className="px-3 pb-2 text-xs text-fair-pink-600 dark:text-fair-pink-400">
                {localErrors.default}
              </div>
            )}
          </div>
          <Footer onCancel={onCancel} submitLabel={submitLabel} cancelLabel={cancelLabel} isSubmitting={isSubmitting} />
        </form>
      );
    }

    default:
      return null;
  }
}

// ============================================================================
// Personalization helpers — toggle row + prompt textarea shown for
// message/inmail/email steps when the rep flips "Personalize per contact"
// on. The static body / subject editors hide; on save, Claude drafts the
// content per contact at the candidate-review stage.
// ============================================================================

/**
 * Touch-campaign step chaining control. When this step completes for a contact,
 * enroll them into the selected campaign. "Do nothing" (empty value) clears the
 * handoff. Only rendered for touch campaigns (see `showTriggerCampaign`).
 */
function TriggerCampaignField({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
}) {
  return (
    <>
      <ContentDivider />
      <FieldRow label="When this step completes">
        <div className="px-3 pb-2" data-testid="cts-trigger-campaign">
          <FilterSelect
            options={[{ value: '', label: 'Do nothing' }, ...options]}
            value={value ?? ''}
            onChange={(v) => onChange(v ? v : null)}
            placeholder="Start another campaign…"
            className="w-full"
          />
        </div>
      </FieldRow>
    </>
  );
}

function PersonalizeToggleRow({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <ToggleFieldRow
      label="Personalize per contact"
      description="AI drafts the message per contact. You review every draft before it sends."
      checked={checked}
      onChange={onChange}
      testId="cts-personalize-toggle"
      toggleTestId="cts-personalize-toggle-switch"
    />
  );
}

function PersonalizationPromptField({
  value,
  onChange,
  modelInfo,
}: {
  value: string;
  onChange: (v: string) => void;
  modelInfo?: { modelId: string } | null;
}) {
  return (
    <FieldRow label="AI prompt">
      <div className="flex flex-col gap-2 px-3 pb-2">
        <Textarea
          data-testid="cts-personalize-prompt"
          placeholder="e.g. Reference their recent LinkedIn post. Mention how our analytics tool fixes attribution for hiring."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
        />
        <PromptContextExplainer modelInfo={modelInfo} />
      </div>
    </FieldRow>
  );
}

/**
 * Collapsible explainer rendered under the AI-prompt textarea on personalized
 * steps. The rep types only the "what to say" prompt — Claude also receives
 * a structured packet of contact + sequence + rep context. We surface that
 * packet here so reps can write tighter prompts (no need to repeat the
 * contact's name or company in the prompt — that's already going in).
 *
 * Kept as a pure presentational disclosure (no platform deps) so the same
 * copy renders in Electron / extension if those surfaces ever expose the
 * step editor.
 */
function PromptContextExplainer({ modelInfo }: { modelInfo?: { modelId: string } | null }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
        <Arrows.ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} />
        What we'll send to the AI along with your prompt
      </button>
      {open && (
        <ul className="mt-2 flex flex-col gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          {modelInfo && (
            <li className="rounded-sm bg-amber-50 px-2 py-1.5 text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:ring-amber-900">
              <span className="font-medium">Model (admin-only):</span>{' '}
              <code className="rounded-sm bg-amber-100 px-1 dark:bg-amber-900/40">{modelInfo.modelId}</code>
              {' — '}configured in the superadmin AI Model settings.
            </li>
          )}
          <li>
            <span className="font-medium text-gray-800 dark:text-gray-200">Contact card</span>
            {' — '}name, headline, current title, industry, location.
          </li>
          <li>
            <span className="font-medium text-gray-800 dark:text-gray-200">Research dossier</span> (when available, from
            the candidates pipeline) — background, career highlights, recent signals from the last ~12 months, hedged
            wealth signals, and suggested talking points the rep can lean on.
          </li>
          <li>
            <span className="font-medium text-gray-800 dark:text-gray-200">Sequence context</span>
            {' — '}the body and subject of the prior template steps in this cadence, so the AI doesn't repeat language
            or contradict an earlier step.
          </li>
          <li>
            <span className="font-medium text-gray-800 dark:text-gray-200">Rep + company</span>
            {' — '}your name, title, calendar link, the team name, and any AI&nbsp;info the team admin configured.
          </li>
          <li>
            <span className="font-medium text-gray-800 dark:text-gray-200">Step constraints</span>
            {' — '}message vs InMail vs email, the character limit, plus formatting rules (no em dashes, no semicolons,
            plain text only).
          </li>
        </ul>
      )}
    </div>
  );
}

export default CTSContentEditingPure;
